import React, { useCallback, useEffect, useRef, useState } from 'react'

import DateInput from '../../components/DateInput'
import { useLocale, useT } from '../../i18n'
import type { Lot } from '../../types/lot'
import { fishStockLogApi } from '../../utils/fishStockLogApi'
import { refreshLotSourcesFromServer } from '../../utils/refreshLotSources'
import { loadStockCountDifferences, type StockCountDifference } from '../../utils/reportDatasets'
import { RECONCILE_FROM_DATE_METADATA_KEY, stockGroupKey } from '../../utils/stockReconciliation'
import { addDays } from '../../utils/lotProjection'
import { formatFishDifference } from '../reports/StockViewExplainer'

// Findings #222: per exact count of a lot, the day "Stock reconciliado" starts
// spreading its difference (`FishStockLog.metadata.reconcileFromDate`). Office
// only, with an explicit save; the count itself never changes. Hidden until a
// count differs from what was expected.

export type LotCountDifferencesSectionProps = {
  lot: Lot
  canEdit: boolean
}

export default function LotCountDifferencesSection({ lot, canEdit }: LotCountDifferencesSectionProps) {
  const t = useT()
  const locale = useLocale()
  const [differences, setDifferences] = useState<StockCountDifference[]>([])
  const [drafts, setDrafts] = useState<Record<string, string>>({})
  const [messages, setMessages] = useState<Record<string, string>>({})

  const loadSeqRef = useRef(0)
  const load = useCallback(async (fromServer = false) => {
    const seq = loadSeqRef.current + 1
    loadSeqRef.current = seq
    if (fromServer) await refreshLotSourcesFromServer(lot.id).catch(() => false)
    const groupKey = stockGroupKey({ lotId: lot.id, lot: lot.lotKey })
    const labelKey = stockGroupKey({ lot: lot.lotKey })
    const all = await loadStockCountDifferences().catch(() => [] as StockCountDifference[])
    if (loadSeqRef.current !== seq) return
    const ofLot = all.filter((entry) => entry.groupKey === groupKey || entry.groupKey === labelKey)
    setDifferences(ofLot)
    setDrafts(Object.fromEntries(ofLot.map((entry) => [entry.logId, entry.fromDay])))
  }, [lot.id, lot.lotKey])

  useEffect(() => {
    setMessages({})
    void load(true)
  }, [load])

  const save = async (entry: StockCountDifference, nextDay: string) => {
    const max = addDays(entry.day, -1)
    if (nextDay && (nextDay < entry.floorDay || nextDay > max)) {
      setMessages((prev) => ({ ...prev, [entry.logId]: t('lots.countDifferences.outOfRange', { min: entry.floorDay, max }) }))
      return
    }
    try {
      const log = await fishStockLogApi.get(entry.logId)
      const { [RECONCILE_FROM_DATE_METADATA_KEY]: _previous, ...metadata } = log?.metadata ?? {}
      await fishStockLogApi.update(entry.logId, {
        metadata: nextDay ? { ...metadata, [RECONCILE_FROM_DATE_METADATA_KEY]: nextDay } : metadata
      })
      await load()
      setMessages((prev) => ({ ...prev, [entry.logId]: t('lots.countDifferences.saved') }))
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)
      setMessages((prev) => ({ ...prev, [entry.logId]: t('lots.countDifferences.saveFailed', { error: message }) }))
    }
  }

  if (differences.length === 0) return null

  const rowStyle: React.CSSProperties = { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 6 }

  return (
    <div data-testid="lots-count-differences" style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: 12 }}>
      <h4 style={{ margin: '0 0 4px 0' }}>{t('lots.countDifferences.title')}</h4>
      <p style={{ margin: 0, fontSize: 13, color: '#475569' }}>{t('lots.countDifferences.subtitle')}</p>
      {differences.map((entry) => (
        <div key={entry.logId} style={{ marginTop: 10 }}>
          <div style={{ fontWeight: 600, fontSize: 14 }}>
            {t('lots.countDifferences.row', { date: entry.day, fish: formatFishDifference(entry.difference, locale) })}
          </div>
          <div style={rowStyle}>
            <span style={{ fontSize: 13 }}>{t('lots.countDifferences.from')}</span>
            {canEdit ? (
              <>
                <DateInput
                  value={drafts[entry.logId] ?? entry.fromDay}
                  onChangeValue={(value) => setDrafts((prev) => ({ ...prev, [entry.logId]: value }))}
                />
                <button
                  type="button"
                  disabled={(drafts[entry.logId] ?? entry.fromDay) === entry.fromDay}
                  onClick={() => { void save(entry, drafts[entry.logId] ?? '') }}
                >
                  {t('lots.countDifferences.save')}
                </button>
                {entry.fromDayIsCustom && (
                  <button type="button" onClick={() => { void save(entry, '') }}>{t('lots.countDifferences.useDefault')}</button>
                )}
              </>
            ) : (
              <strong style={{ fontSize: 13 }}>{entry.fromDay}</strong>
            )}
            <span style={{ fontSize: 12, color: '#64748b' }}>{t('lots.countDifferences.fromDefault', { date: entry.floorDay })}</span>
          </div>
          {messages[entry.logId] && <div style={{ fontSize: 12, color: '#475569', marginTop: 4 }}>{messages[entry.logId]}</div>}
        </div>
      ))}
    </div>
  )
}
